export function summarizePhoneCart(cart, eligibleTypes, discountTitle) {
  const prices = [];
  let savings = 0;
  let freeCount = 0;
  let otherDiscount = (cart.cart_level_discount_applications || []).some(discount => discount.title !== discountTitle);
  for (const item of cart.items || []) {
    if (!eligibleTypes.includes(item.product_type)) continue;
    const price = Number(item.original_price ?? item.price);
    const quantity = Number(item.quantity);
    if (!Number.isFinite(price) || price <= 0 || !Number.isInteger(quantity) || quantity <= 0) continue;
    for (let i = 0; i < quantity; i++) prices.push(price);
    let itemSaving = 0;
    for (const allocation of item.line_level_discount_allocations || []) {
      if (allocation.discount_application?.title === discountTitle) itemSaving += Number(allocation.amount) || 0;
      else if (Number(allocation.amount) > 0) otherDiscount = true;
    }
    savings += itemSaving;
    freeCount += Math.min(quantity, Math.floor(itemSaving / price));
  }
  prices.sort((a, b) => a - b);
  return {
    count: prices.length,
    nextIsFree: prices.length % 2 === 1,
    freePriceLimit: prices[Math.floor(prices.length / 2)] || 0,
    savings,
    freeCount,
    otherDiscount,
    currency: typeof cart.currency === 'string' ? cart.currency : cart.currency?.iso_code || 'GBP',
  };
}

export function productOffer(price, state, money) {
  if (!Number.isFinite(price) || price <= 0) return null;
  if (state.nextIsFree) {
    const canPromiseFree = !state.otherDiscount && (state.count === 1 || state.savings > 0);
    const free = canPromiseFree && price <= state.freePriceLimit;
    return {
      title: free ? 'Add this case - it\'s FREE' : 'Complete your pair and save',
      message: free
        ? `You have ${state.count} ${state.count === 1 ? 'case' : 'cases'} in your basket. The ${state.count === 1 ? '2nd' : 'next'} is free.`
        : 'Add this case; the lowest-priced eligible case is free. Your basket confirms the saving.',
      progress: 1,
    };
  }
  if (state.freeCount > 0) {
    return {
      title: `${state.freeCount} free ${state.freeCount === 1 ? 'case' : 'cases'} unlocked`,
      message: `Add 2 more to repeat - only ${money(Math.round(price / 2))} each`,
      progress: 0,
    };
  }
  return {
    title: `Only ${money(Math.round(price / 2))} each when you buy 2`,
    message: state.count > 0
      ? 'Mix and match another pair. The lowest-priced case is free.'
      : 'Mix and match designs. Your 2nd case is free.',
    progress: state.count > 0 ? 2 : 0,
  };
}

export function cartOffer(state, money) {
  if (state.nextIsFree) {
    return {
      title: state.otherDiscount ? 'Complete your phone-case pair' : 'Add 1 more case - it\'s FREE',
      message: state.otherDiscount
        ? 'Buy 1, get 1 free is available on eligible cases. Other discounts may not combine; your basket confirms the saving.'
        : `${state.count} eligible ${state.count === 1 ? 'case' : 'cases'} in your basket. Add one more; the lowest-priced case is free.`,
      progress: 1,
      pickAnother: true,
    };
  }
  return {
    title: state.freeCount > 0 ? `${state.freeCount} FREE ${state.freeCount === 1 ? 'case' : 'cases'} unlocked` : 'Buy 1, get 1 free',
    message: state.savings > 0
      ? `You've saved ${money(state.savings)} on phone cases.`
      : 'The lowest-priced eligible case is free. Your basket shows which offer applies.',
    progress: state.count > 0 ? 2 : 0,
    pickAnother: false,
  };
}

if (typeof window !== 'undefined' && !customElements.get('dyefor-phone-offer')) {
  let config;
  let state;
  let revision = 0;
  let refreshPromise;
  const changed = 'dyefor:phone-offer-state';
  const money = cents => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: state?.currency || window.Shopify?.currency?.active || 'GBP',
      }).format(cents / 100);
    } catch {
      return `${state?.currency || 'GBP'} ${(cents / 100).toFixed(2)}`;
    }
  };

  function update(cart) {
    if (!config) return;
    revision++;
    state = summarizePhoneCart(cart, config.eligibleTypes, config.discountTitle);
    document.dispatchEvent(new CustomEvent(changed));
    const copy = cartOffer(state, money);
    for (const badge of document.querySelectorAll('[data-phone-free-count]')) {
      badge.hidden = state.freeCount === 0;
      badge.querySelector('span').textContent = `${state.freeCount} free ${state.freeCount === 1 ? 'case' : 'cases'}`;
    }
    for (const prompt of document.querySelectorAll('[data-phone-cart-prompt]')) {
      prompt.hidden = state.count === 0;
      prompt.dataset.caseCount = state.count;
      prompt.dataset.pairComplete = String(!state.nextIsFree && state.freeCount > 0);
      prompt.querySelector('[data-phone-cart-title]').textContent = copy.title;
      prompt.querySelector('[data-phone-cart-message]').textContent = copy.message;
      prompt.querySelector('[data-phone-cart-progress]').value = copy.progress;
      prompt.querySelector('[data-phone-cart-link]').hidden = !copy.pickAnother;
      prompt.querySelector('[data-phone-cart-link]').firstChild.textContent = state.otherDiscount ? 'Explore phone cases ' : 'Pick your free case ';
    }
  }

  async function refresh() {
    if (!config || refreshPromise) return refreshPromise;
    const version = revision;
    const root = window.Shopify?.routes?.root || '/';
    refreshPromise = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(response => {
        if (!response.ok) throw new Error('Cart unavailable');
        return response.json();
      })
      .then(cart => { if (revision === version) update(cart); })
      .catch(() => {
        if (revision !== version) return;
        // A failed background read must not break checkout or claim a stale free case.
        state = undefined;
        document.dispatchEvent(new CustomEvent(changed));
        document.querySelectorAll('[data-phone-cart-prompt]').forEach(prompt => { prompt.hidden = true; });
        document.querySelectorAll('[data-phone-free-count]').forEach(badge => { badge.hidden = true; });
      })
      .finally(() => { refreshPromise = undefined; });
    return refreshPromise;
  }

  class PhoneCartState extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected) return;
        try {
          const cart = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
          config = { eligibleTypes: cart.eligibleTypes, discountTitle: cart.discountTitle };
          update(cart);
        } catch { /* Keep the server-rendered offer when its snapshot cannot be read. */ }
      });
    }
  }

  class PhoneOffer extends HTMLElement {
    connectedCallback() {
      this.renderBound = () => this.render();
      document.addEventListener(changed, this.renderBound);
      this.render();
    }

    disconnectedCallback() { document.removeEventListener(changed, this.renderBound); }

    render() {
      const current = state || { count: 0, nextIsFree: false };
      const copy = productOffer(Number(this.dataset.offerPrice), current, money);
      if (!copy) return;
      this.dataset.caseCount = current.count;
      this.querySelector('[data-phone-offer-title]').textContent = copy.title;
      this.querySelector('[data-phone-offer-message]').textContent = copy.message;
      const progress = this.querySelector('[data-phone-offer-progress]');
      progress.hidden = current.count === 0;
      progress.value = copy.progress;
    }
  }

  class CartAdded extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('cart-drawer');
      this.querySelector('[data-added-close]').addEventListener('click', () => this.drawer.close());
      this.querySelector('[data-added-view-cart]').addEventListener('click', event => {
        event.preventDefault();
        this.drawer.classList.remove('is-added-notice');
        this.drawer.open();
      });
    }

    prepare(response) {
      const cart = JSON.parse(this.drawer.querySelector('dyefor-phone-cart-state script').textContent);
      const addedItems = typeof response.item_count === 'number' ? [] : (response.items || [response]);
      const added = addedItems.find(item => cart.eligibleTypes.includes(item.product_type));
      if (added) this.drawer.addedCase = { key: added.key, variant: added.variant_id || added.id };
      else if (addedItems.some(item => item.handle !== 'dyefor-shipping-protection')) return false;
      else if (!this.drawer.classList.contains('is-added-notice')) return false;

      const reference = this.drawer.addedCase;
      if (!reference) return false;
      const item = cart.items.find(line => line.key === reference.key)
        || cart.items.find(line => line.variant_id === reference.variant && cart.eligibleTypes.includes(line.product_type));
      if (!item) return false;
      const line = this.drawer.querySelector(`#CartDrawer-Item-${item.index + 1}`);
      if (!line) return false;
      const sourceImage = line.querySelector('.cart-item__image');
      const image = this.querySelector('[data-added-image]');
      image.replaceChildren();
      if (sourceImage) {
        const clone = sourceImage.cloneNode(true);
        delete clone.dataset.dyeforCartPreviewErrorBound;
        clone.loading = 'eager';
        image.append(clone);
      }
      const title = line.querySelector('.cart-item__name');
      this.querySelector('[data-added-title]').textContent = title.textContent;
      this.querySelector('[data-added-title]').href = title.href;
      this.querySelector('[data-added-options]').textContent = [...line.querySelectorAll('.dyefor-cart-item__meta')].map(el => el.textContent.trim().replace(/\s+/g, ' ')).join(' / ');
      this.querySelector('[data-added-quantity]').textContent = item.quantity > 1 ? `Quantity: ${item.quantity}` : '';
      const original = this.querySelector('[data-added-original]');
      original.hidden = item.original_line_price <= item.final_line_price;
      original.textContent = money(item.original_line_price);
      const current = this.querySelector('[data-added-price]');
      current.textContent = item.final_line_price === 0 ? 'FREE' : money(item.final_line_price);
      current.classList.toggle('is-free', item.final_line_price === 0);

      const summary = summarizePhoneCart(cart, cart.eligibleTypes, cart.discountTitle);
      const copy = cartOffer(summary, money);
      this.dataset.caseCount = summary.count;
      this.dataset.pairComplete = String(!summary.nextIsFree && summary.freeCount > 0);
      this.querySelector('[data-added-offer]').textContent = copy.title;
      this.querySelector('[data-added-progress]').value = copy.progress;
      const free = this.querySelector('[data-added-free]');
      free.hidden = summary.freeCount === 0;
      free.querySelector('span').textContent = `${summary.freeCount} free ${summary.freeCount === 1 ? 'case' : 'cases'}`;
      this.querySelector('[data-added-shipping]').hidden = !cart.shipping?.available || cart.shipping.remaining > 0;
      const pick = this.querySelector('[data-added-pick]');
      pick.hidden = !copy.pickAnother;
      pick.textContent = summary.otherDiscount ? 'Explore phone cases' : 'Pick your FREE case';
      const checkout = this.querySelector('[data-added-checkout]');
      checkout.classList.toggle('is-secondary', copy.pickAnother);
      checkout.querySelector('span').textContent = copy.pickAnother ? `Checkout with ${summary.count} ${summary.count === 1 ? 'case' : 'cases'}` : 'Checkout';
      const wallets = this.querySelector('[data-added-wallets]');
      wallets.hidden = copy.pickAnother;
      return true;
    }
  }

  customElements.define('dyefor-phone-offer', PhoneOffer);
  customElements.define('dyefor-phone-cart-state', PhoneCartState);
  customElements.define('dyefor-cart-added', CartAdded);
  // Cart section replacements supply fresh snapshots, including personalised additions.
  window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refresh(); });
}
